import { Directive, ElementRef, HostListener } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appDecimalInput]'
})
export class DecimalInputDirective {
  constructor(private control: NgControl, private el: ElementRef) {}

  private specialKeys = [
    'Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete'
  ];

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = this.el.nativeElement as HTMLInputElement;

    if (this.specialKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;

    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const raw = input.value.replace(/,/g, '');

    const nextValue =
      raw.substring(0, selectionStart) +
      event.key +
      raw.substring(selectionEnd);

    if (!this.isValidDecimalFormat(nextValue) || !this.isInRange(nextValue, true)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const inputEl = this.el.nativeElement as HTMLInputElement;
    const rawValue = inputEl.value.replace(/,/g, '');

    const oldSelectionStart = inputEl.selectionStart ?? 0;
    const oldLength = inputEl.value.length;

    let newValue = rawValue;

    if (newValue.trim() === '') {
      this.control.control?.setValue('');
      inputEl.value = '';
      return;
    }
    if (!this.isValidDecimalFormat(rawValue) || !this.isInRange(rawValue, true)) {
      newValue = this.control.control?.value ?? '';
    }

    this.control.control?.setValue(newValue);

    const formatted = this.formatWithCommas(newValue);
    inputEl.value = formatted;

    const newLength = formatted.length;
    const cursorShift = newLength - oldLength;

    const newCursor = oldSelectionStart + cursorShift;
    inputEl.setSelectionRange(newCursor, newCursor);
  }

  @HostListener('blur')
  onBlur(): void {
    let value = this.control.control?.value;

    if (!this.isValidDecimalFormat(value) || !this.isInRange(value, false)) {
      this.control.control?.setValue('');
      value = '';
    }

    this.el.nativeElement.value = this.formatWithCommas(value);
  }

  private isValidDecimalFormat(value: string): boolean {
    return /^(0|[1-9]\d{0,2})(\.\d{0,6})?$/.test(value);
  }

  private isInRange(value: string, isTyping: boolean = false): boolean {
    if (!value || value === '.' || value === '0.') return true; 

    const num = Number(value);
    if (isNaN(num)) return false;

    if (isTyping) return num >= 0 && num <= 100;

    return num >= 0.01 && num <= 100;
  }

  private formatWithCommas(value: string): string {
    if (!value) return '';
    const [intPart, decPart] = value.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
  }
}
